import { FC } from 'react';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { MarkIcon } from '@bizapp-lp/workflow/atoms/Icon';

export interface QuestionAccordionProps {
  id: string;
  expanded?: boolean;
  question: string;
  onChange?: (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  className?: string;
  children?: React.ReactNode;
}

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      boxShadow: '0px 8px 16px #00000014',
      transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',

      '&.MuiAccordion-root': {
        position: 'static',
        '&.Mui-expanded': {
          margin: theme.spacing(3, 'auto', 0),
        },
        '&:hover': {
          boxShadow: '0px 8px 16px #00000033',
          '& .MuiAccordionSummary-expandIcon .MuiBox-root svg': {
            color: '#333333',
          },
        },
        '&:active': {
          boxShadow: '0px 8px 16px #00000033',
          '& .MuiAccordionSummary-expandIcon .MuiBox-root svg': {
            color: '#333333',
          },
        },
      },
      '& .MuiAccordionSummary-root': {
        borderRadius: '8px',
        padding: '0 32px',
        '& .MuiIconButton-root': { padding: 0 },
        '&.Mui-focused': { backgroundColor: '#FFFFFF' },
        '&:active': {
          '& .MuiAccordionSummary-expandIcon .MuiBox-root': {
            backgroundColor: '#C2CAD8',
            '& svg': { color: '#333333' },
          },
        },
        '&:focus': {
          '& .MuiAccordionSummary-expandIcon .MuiBox-root': {
            backgroundColor: '#EDEFF3',
            '& svg': { color: '#333333' },
          },
        },
      },
      '& .MuiAccordionSummary-content': {
        margin: 0,
        minHeight: '72px',
        height: '100%',
        '& .MuiTypography-subtitle1': {
          color: '#333333',
          fontSize: '20px',
          fontWeight: 'bold',
          lineHeight: '30px',
        },
      },
      '& .MuiAccordionDetails-root': {
        padding: theme.spacing(1, 2, 21 / 8, 4),
        '& p': {
          color: '#333333',
          fontSize: '20px',
          lineHeight: '30px',
          marginTop: 0,
          marginBottom: theme.spacing(2),
          '&:last-child': {
            marginBottom: 0,
          },
          '& span': {
            display: 'block',
          },
        },
        '& ul': {
          color: '#333333',
          fontSize: '20px',
          lineHeight: '30px',
          marginTop: 0,
          marginBottom: theme.spacing(2),
          paddingLeft: theme.spacing(3),
          '&:last-child': {
            marginBottom: 0,
          },
        },
      },
    },
    qIcon: { marginTop: theme.spacing(2) },
    qText: { margin: theme.spacing(21 / 8, 0, 21 / 8, 2) },
    aText: {
      margin: theme.spacing(5 / 8, 0, 0, 2),
      '& .MuiLink-root': {
        color: '#1565C0',
      },
    },
    expandIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      transition:
        'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      '& > svg': {
        color: '#7F7F7F',
        marginTop: `${(48 - 24) / 2}px`,
      },
    },
  }),
);

export const QuestionAccordion: FC<QuestionAccordionProps> = ({
  id,
  expanded,
  question,
  onChange,
  className = '',
  children,
}: QuestionAccordionProps) => {
  const classes = useStyles();

  return (
    <Accordion
      square
      id={id}
      expanded={expanded}
      onChange={onChange}
      className={`${classes.root} ${className}`}
    >
      <AccordionSummary
        expandIcon={
          <Box className={classes.expandIcon}>
            <ExpandMoreIcon />
          </Box>
        }
        IconButtonProps={{ disableRipple: true }}
        onFocus={(e) => {
          const root = document.getElementById(id);
          root?.setAttribute('style', 'box-shadow: 0px 8px 16px #00000033;');
        }}
        onBlur={(e) => {
          const root = document.getElementById(id);
          root?.removeAttribute('style');
        }}
      >
        <Box className={classes.qIcon}>
          <MarkIcon markType="question" />
        </Box>
        <Box className={classes.qText}>
          <Typography variant="subtitle1">{question}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <MarkIcon markType="answer" />
        </Box>
        <Box className={classes.aText}>{children}</Box>
      </AccordionDetails>
    </Accordion>
  );
};
